/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useEffect, useState } from 'react';
import { useAuth } from '@core/hooks/useAuth';
import { useTanstackQuery } from '@core/hooks/useTanstackQuery';
import { environmentService } from '@core/services/Environment.service';
import { tanstackQueryService } from '@core/services/TanstackQuery.service';
import { tokenService } from '@core/services/Token.service';
import { WHO_AM_I_QUERY_KEY } from '@shared/constants/whoAmI.constant';
import { loginMutation } from './mutations/login.mutation';
import AbcloudzFlowChat from './view/AbcloudzFlowChat';
import Loading from './view/Loading';
import WrapperErrorBlock from './view/WrapperErrorBlock';
import type { TAbcloudzError, TResponseError } from './models/abcloudzFlowChat.model';
import type { TStatusMap } from './models/statusMap.models';
import type { TWhoAmIResponse } from './models/whoAmI.model';
import type { TNullable } from '@core/models/utility.model';
import type { AxiosResponse } from 'axios';

const WrapperAbcloudzFlowChat = () => {
  const { isAuthenticated } = useAuth();

  const flowId = environmentService.flowId;
  const clientId = environmentService.clientId;
  const refreshToken = tokenService.getRefreshToken;

  const [status, setStatus] = useState<TStatusMap>('unknown');
  const statusMap: Record<TStatusMap, () => TNullable<React.JSX.Element>> = {
    pending: () => <Loading />,
    failed: () => (
      <WrapperErrorBlock
        error={isError}
        onRetry={() => {
          void onLogin();
        }}
      />
    ),
    flowChat: () => (flowId ? <AbcloudzFlowChat flowId={flowId} /> : null),
    unknown: () => null,
    unauthorized: () => (
      <WrapperErrorBlock
        error={{
          title: 'Session Expired',
          message: 'Please log in to use the chat.'
        }}
        onRetry={() => {
          window.location.reload();
        }}
      />
    )
  };

  const [isError, setIsError] = useState<TNullable<TAbcloudzError>>(null);

  const login = useTanstackQuery({
    instance: loginMutation.login$
  });

  const onLogin = async () => {
    try {
      setIsError(null);
      setStatus('pending');

      await login.mutate(
        { dto: { clientId, refreshToken }, silent: true },
        {
          onSuccess: ({ data }) => {
            tanstackQueryService.queryClient.setQueryData<AxiosResponse<TWhoAmIResponse>>(
              [WHO_AM_I_QUERY_KEY],
              { data: { user: data.user } } as unknown as AxiosResponse<TWhoAmIResponse>
            );

            tokenService.setTokens(data.tokens);
            setStatus('flowChat');
          },
          onError: (error) => {
            const errorData = error.response?.data as TResponseError;

            const title = errorData?.error ?? 'Oops! Something went wrong.';
            const errorMessage = errorData?.message ?? '';
            const validationErrors = Object.entries(errorData?.validationErrors ?? {})
              .map(([_, value]) => value.join(', '))
              .join(', ');
            const message = `${errorMessage} ${validationErrors}`.trim();

            tokenService.removeTokens();
            setIsError({
              title,
              message: message
                ? message
                : 'We encountered an unexpected issue while processing your request.'
            });
            setStatus('failed');
          }
        }
      );
    } catch (_error) {
      // Handle error here as needed
    }
  };

  useEffect(() => {
    if (!isAuthenticated && status === 'flowChat') {
      setStatus('unauthorized');
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void onLogin();
  }, []);

  return statusMap[!isAuthenticated && status !== 'pending' ? 'unauthorized' : status]();
};

export default WrapperAbcloudzFlowChat;
